import { useCallback, useContext, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Link } from 'react-router';
import { store } from 'components/Store';
import { validateEmail } from 'utils/validateEmail';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import parliamentarianUsers, { dataHubUsers } from 'components/Dashboard/SidebarElements/parliamentarianUsers';
import SalutationModal from 'components/Dashboard/Influence/CrmContacts/SalutationModal';
import ChampionStatus from 'components/Dashboard/Influence/tableComponents/ChampionStatus';
import CustomFieldItem from 'components/Dashboard/StakeHolders/StakeholderPage/ui/CustomFieldItem';
import CollapsibleSection from 'components/Dashboard/StakeHolders/StakeholderPage/ui/CollapsibleSection';
import BasicInformation from 'components/Dashboard/StakeHolders/StakeholderPage/ui/BioSections/BasicInformation';
import TagsSection from 'components/Dashboard/StakeHolders/StakeholderPage/ui/BioSections/TagsSection';
import Biography from 'components/Dashboard/StakeHolders/StakeholderPage/ui/BioSections/Biography';
import Staffers from 'components/Dashboard/StakeHolders/StakeholderPage/ui/BioSections/Staffers';

const sortAlphabetically = (items) =>
  items && [...items].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

const AboutTab = (props) => {
  const {
    StakeholderInformation,
    activePlan,
    setShowContactsPopup,
    updateChampionStatus,
    bioElementsScrollBarPosition,
  } = props;
  const {
    contactInformation,
    positions,
    profile,
    staff,
    categories,
    selectCommittees,
    lordsSelectCommittees,
    allPartyGroups,
    crossPartyGroups,
    constituency,
    party,
    email,
    website,
  } = StakeholderInformation;

  const globalState = useContext(store);
  const { team } = globalState.state;
  const { isAuthenticated } = useAuth0();
  const { newAgencyPlan } = useTopicOrClientWord();
  const { generalApiCall } = useGeneralApiCall();
  const { setContentResults } = useContentResults();
  const { code } = activePlan;

  const isParliamentarian = parliamentarianUsers(code);
  const dataHubUser = dataHubUsers(activePlan);
  const crmContact = contactInformation?.[0];
  const basicInfoCondition =
    constituency !== null ||
    party !== null ||
    email !== null ||
    website !== null ||
    (!isParliamentarian && !!crmContact);
  const westminsterMember = categories?.includes('MP') || categories?.includes('Lord');

  const saveCrmContactField = useCallback(
    async (fieldKey, value) => {
      const contact = contactInformation?.[0];
      if (!contact) return;
      if (fieldKey === 'emailAddress' && value && !validateEmail(value)) {
        createNotification('danger', 'Please enter a valid email address');
        throw new Error('Invalid email');
      }
      const result = await generalApiCall({
        pathname: '/api/crm-contact/update',
        method: 'post',
        requestProperties: {
          id: contact.id,
          [fieldKey]: fieldKey === 'emailAddress' && !value ? '' : value,
        },
        needsAuthentication: true,
        returnCompleteRequest: true,
      });
      if (result?.status === 200) {
        const originalKey = fieldKey === 'salutation' ? 'originalSalutation' : 'originalEmailAddress';
        const contactField = fieldKey === 'salutation' ? 'salutation' : 'email';
        const isEmailField = fieldKey !== 'salutation';
        let updatedFields;
        if (!value) {
          updatedFields = {
            [contactField]: contact[originalKey] ?? null,
            ...(isEmailField ? { isCustomEmailAddress: false } : { isCustomSalutation: false }),
          };
        } else if (contact[originalKey] == null) {
          updatedFields = {
            [contactField]: value,
            [originalKey]: contact[contactField] ?? null,
            ...(isEmailField ? { isCustomEmailAddress: true } : { isCustomSalutation: true }),
          };
        } else {
          updatedFields = {
            [contactField]: value,
            ...(isEmailField ? { isCustomEmailAddress: true } : { isCustomSalutation: true }),
          };
        }
        setContentResults((prev) => {
          const Stakeholders = prev?.Stakeholders;
          const newContactInfo = [...(Stakeholders?.contact?.contactInformation ?? [])];
          newContactInfo[0] = { ...newContactInfo[0], ...updatedFields };
          return {
            ...prev,
            Stakeholders: {
              ...Stakeholders,
              contact: { ...Stakeholders?.contact, contactInformation: newContactInfo },
            },
          };
        });
        createNotification('success', `${fieldKey === 'salutation' ? 'Salutation' : 'Email'} updated successfully`);
      } else {
        if (result?.status === 400) {
          const msg = typeof result?.data === 'string' ? result.data : 'Could not save changes';
          createNotification('danger', msg);
        }
        throw new Error('Save failed');
      }
    },
    [contactInformation]
  );

  return (
    <>
      <div
        className={`${basicInfoCondition ? 'px-4 border-bottom pb-3' : 'basic-information-empty-adjustment'} ${
          isParliamentarian ? 'pt-4' : ''
        }`}
      >
        <BasicInformation
          StakeholderInformation={StakeholderInformation}
          crmEmail={!isParliamentarian ? crmContact : null}
          onSaveEmail={(value) => saveCrmContactField('emailAddress', value)}
        />
        {isAuthenticated && !isParliamentarian && !!crmContact && (
          <CustomFieldItem
            labelNode={<SalutationHeader />}
            name='salutation'
            initialValue={crmContact.salutation}
            onSave={(value) => saveCrmContactField('salutation', value)}
          />
        )}
      </div>

      {isAuthenticated && !isParliamentarian && !!crmContact && (
        <div className='px-4 border-bottom py-4'>
          <h4 className='category-subtitle member-info-subtitle'>
            Champion status {newAgencyPlan ? `for ${team?.teamName}` : ''}{' '}
            {dataHubUser && (
              <span
                className='icon-padlock ml-2 text-main-blue-40 paragraph-p3 d-inline-block'
                style={{ transform: 'translateY(1px)' }}
              />
            )}
          </h4>
          <div className='pt-1 col-lg-8 px-0'>
            <ChampionStatus
              updateChampionStatus={updateChampionStatus}
              scrollBarPosition={bioElementsScrollBarPosition}
              contact={crmContact}
              disabled={dataHubUser}
            />
          </div>
        </div>
      )}

      {!isParliamentarian && (
        <TagsSection
          newAgencyPlan={newAgencyPlan}
          contactInformation={contactInformation}
          setShowContactsPopup={setShowContactsPopup}
          StakeholderInformation={StakeholderInformation}
          isAuthenticated={isAuthenticated}
          isAddedToMyContacts={contactInformation?.length > 0}
        />
      )}
      <ListSection title='Positions' items={positions} icon='star light-75-text' itemClassName='position-subtitle' />
      {!!profile && (
        <Biography
          StakeholderInformation={StakeholderInformation}
          profile={profile}
          isAuthenticated={isAuthenticated}
          setShowContactsPopup={setShowContactsPopup}
        />
      )}
      {staff?.length > 0 && <Staffers staff={staff} />}
      {westminsterMember && (
        <>
          <ListSection title='Select Committees' items={selectCommittees} />
          <ListSection title='Lords Select Committees' items={lordsSelectCommittees} />
          <ListSection title='APPGs' items={sortAlphabetically(allPartyGroups)} />
        </>
      )}
      <ListSection
        title={categories?.includes('MLA') ? 'All Party Groups' : 'Cross Party Groups'}
        items={sortAlphabetically(crossPartyGroups)}
      />

      {!isAuthenticated && (
        <div className={`px-4 pt-3 border-bottom`}>
          <h4 className={`category-subtitle member-info-subtitle`}>Have your voice heard</h4>
          <p className={`biography-element mb-2`}>
            Hundreds of MPs, peers and advisers use PolicyMogul to find lobbying material added by campaigners and
            organisations
          </p>
          <p className='pb-4 mb-0'>
            <Link
              to='/register?reg_source=stakeholder_page_add-lobbying-material'
              className='simple-link-button paragraph-p2'
            >
              Add your lobbying material to PolicyMogul
            </Link>
          </p>
        </div>
      )}
    </>
  );
};

const SalutationHeader = () => {
  const [showModal, setShowModal] = useState(false);
  return (
    <>
      <SalutationModal hideSalutationModal={() => setShowModal(false)} showSalutationModal={showModal} />
      <h4
        className='category-subtitle member-info-subtitle d-flex align-items-center'
        style={{ cursor: 'pointer', width: 'fit-content' }}
        onClick={() => setShowModal(true)}
      >
        <span>Salutation</span>
        <span className='icon-info pointer specific-left-margin-6 light-40-text paragraph-p3 d-inline-block specific-top-padding-2' />
      </h4>
    </>
  );
};

// A collapsible list of positions or group memberships; renders nothing when there are none
const ListSection = ({ title, items, icon = 'group light-60-text', itemClassName = '' }) => {
  if (!items?.length) {
    return null;
  }

  return (
    <CollapsibleSection title={title} titleClassName='feature-subtitle'>
      <div className='pt-1'>
        {items.map((item, index) => (
          <div className='my-2 member-data' key={`comittee-${index}`}>
            {/* The icon sits in its own column so a wrapped name lines up with its first line */}
            <p className={`mb-0 committe-subtitle d-flex ${itemClassName}`}>
              <span className='flex-shrink-0'>
                <span className={`icon-${icon} d-inline-block-centered centered-5 specific-right-margin-10`} />
              </span>
              <span>{item}</span>
            </p>
          </div>
        ))}
      </div>
    </CollapsibleSection>
  );
};

export default AboutTab;
